import { createApi } from '@reduxjs/toolkit/query/react';

import fetchBaseQuery from '../fetchBaseQuery';

/* eslint-disable @typescript-eslint/naming-convention */
export type Filename = {
    filename_id: string;
    filename: string;
};

export type GetFilenamesResponse = {
    err: 1 | 0;
    data: Filename[];
};

export type GetTagsResponse = {
    tags: string[];
};

export type GetIntegrationFileImportResponse = {
    result: 'success';
    triggered: boolean;
    keysAvailable: boolean;
};

export type GetKeyStatsRequest = {
    projectId: string;
    uniqueScrollerId: string;
    params: {
        view: string;
        reference_lang_id: number | undefined;
        single_lang_id: number | undefined;
        search: string;
        search_case_sensitive: 0 | 1;
        exact_match: 0 | 1;
        filter: unknown;
        reset_filter: 0 | 1;
        screenshots_filter: unknown;
        reset_screenshots_filter: 0 | 1;
        sort_mode: string;
        document_id: string;
        branch: string;
    };
};

export type GetKeyStatsResponse = {
    source_word_count: number;
    count_total: number;
    filename_ids_distinct_count: number;
};
/* eslint-enable @typescript-eslint/naming-convention */

const projectApi = createApi({
    reducerPath: 'projectApi',
    baseQuery: fetchBaseQuery(),
    tagTypes: [],
    endpoints: (builder) => ({
        getJwtToken: builder.query<{ reviewCenterBackendHost: string; token: string }, { projectId: string }>({
            query: ({ projectId }) => ({
                method: 'GET',
                url: `/project/${projectId}/review-center/token/generate`,
            }),
        }),
        getFilenames: builder.query<GetFilenamesResponse, { projectId: string; branchName?: string }>({
            query: ({ projectId, branchName }) => ({
                method: 'GET',
                url: `/project/${projectId}/filenames${branchName ? `?branch=${branchName}` : ``}`,
            }),
        }),
        getTags: builder.query<GetTagsResponse, { projectId: string; branchName?: string }>({
            query: ({ projectId, branchName }) => ({
                method: 'GET',
                url: `/project/${projectId}/tags${branchName ? `?branch=${branchName}` : ``}`,
            }),
        }),
        getKeyStats: builder.query<GetKeyStatsResponse, GetKeyStatsRequest>({
            query: ({ projectId, params }) => ({
                method: 'GET',
                url: `/project/${projectId}/key-stats`,
                params,
            }),
        }),
        setIntegrationFileImport: builder.mutation<GetIntegrationFileImportResponse, { projectId: string }>({
            query: ({ projectId }) => ({
                method: 'POST',
                url: `/project/${projectId}/integration-file-import`,
            }),
        }),
        getIntegrationFileImport: builder.query<GetIntegrationFileImportResponse, { projectId: string }>({
            query: ({ projectId }) => ({
                method: 'GET',
                url: `/project/${projectId}/integration-file-import`,
            }),
        }),
    }),
});

export default projectApi;

export const {
    useGetFilenamesQuery,
    useGetTagsQuery,
    useLazyGetJwtTokenQuery,
    useLazyGetKeyStatsQuery,
    useGetIntegrationFileImportQuery,
    useSetIntegrationFileImportMutation,
} = projectApi;
